import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { Alert, Box, Stack, TextField, Typography } from '@mui/material';

import { CancelButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';
import {
  getSkillxStudents,
  getSkillxStudentsSummary,
  updateSkillxId,
} from '../../../redux/features/skillxSlice';

/**
 * Add or change one learner's SkillX ID — the body of the page's dialog.
 *
 * The same form does both. The row either has an ID or it does not, and that
 * only changes the wording; a separate "Add" component would be this form
 * twice. It renders as dialog CONTENT rather than owning a dialog of its own,
 * because GlobalTable's action cells are declarative buttons that report a
 * click upward — the page holds the one dialog they all open.
 *
 * The ID is issued outside this ERP and typed in by hand, so nothing here can
 * check it is the RIGHT ID. What the server does check is that it is not
 * already someone else's; that refusal is shown as it comes back rather than
 * being second-guessed here.
 */

const ValidationSchema = Yup.object().shape({
  skillx_id: Yup.string()
    .trim()
    .max(30, 'A SkillX ID can be at most 30 characters')
    .required('Enter the SkillX ID'),
});

export default function UpdateSkillxId({ row, dispatchparams, onClose }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);

  const existing = row?.skillx_id || '';
  const isAdd = !existing;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { skillx_id: existing },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      dispatch(
        updateSkillxId({ admn_no: row?.admn_no, skillx_id: values.skillx_id.trim() }),
      ).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          dispatch(getSkillxStudents(dispatchparams));
          dispatch(
            getSkillxStudentsSummary({
              fromDate: dispatchparams?.fromDate,
              toDate: dispatchparams?.toDate,
            }),
          );
          onClose();
        } else {
          enqueueSnackbar(res.payload?.message || 'Could not save the SkillX ID', {
            variant: 'error',
          });
        }
      });
    },
  });

  const { errors, touched, values, handleChange, handleSubmit } = formik;
  const changed = values.skillx_id.trim() !== existing;

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit}>
        <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Admission number" fullWidth disabled value={row?.admn_no || ''} />
            <TextField label="Student" fullWidth disabled value={row?.student_name || ''} />
          </Stack>

          <TextField
            name="skillx_id"
            label="SkillX ID"
            fullWidth
            autoFocus
            value={values.skillx_id}
            onChange={handleChange}
            error={touched.skillx_id && Boolean(errors.skillx_id)}
            helperText={
              (touched.skillx_id && errors.skillx_id) ||
              'Exactly as it was issued — it is stored and matched as typed.'
            }
          />

          {/* Worth saying before somebody replaces an ID that is already in use
              elsewhere: this column is what a SkillX learner is known by outside
              the ERP. */}
          {!isAdd && changed && (
            <Alert severity="warning" sx={{ fontSize: 12.5 }}>
              This student is currently linked as <b>{existing}</b>. Saving replaces that link —
              anything already recorded against the old ID stays where it is.
            </Alert>
          )}

          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            A SkillX ID belongs to one student only. If it is already linked to somebody else,
            the save is refused and you will be told who has it.
          </Typography>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
          <Stack direction="row" spacing={1}>
            <CancelButton size="small" action={onClose}>
              Cancel
            </CancelButton>
            <UpdateButton disabled={loading || !changed} size="small" type="submit">
              {loading ? 'Please wait...' : isAdd ? 'Add' : 'Update'}
            </UpdateButton>
          </Stack>
        </Box>
      </Form>
    </FormikProvider>
  );
}
